// apiFetch.js
// 统一封装fetch，自动带token，用于全站接口调用

import siteConfig from '../config/site-config.js';

// API基础URL配置 - 使用统一配置
const API_BASE_URL = siteConfig.getApiUrl();

// 防止重复弹窗 / 过期后死循环硬跳转
let isShowingLoginPrompt = false;
let authExpiredLatched = false;

const AUTH_EXPIRED_KEY = 'zhxt_auth_expired';

function readExpiredLatch() {
  if (authExpiredLatched) return true;
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(AUTH_EXPIRED_KEY) === '1') {
      authExpiredLatched = true;
      return true;
    }
  } catch (_) { /* ignore */ }
  return false;
}

function setExpiredLatch() {
  authExpiredLatched = true;
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(AUTH_EXPIRED_KEY, '1');
  } catch (_) { /* ignore */ }
}

/** 登录成功后清除过期闩锁，允许再次走 401 提示 */
export function clearAuthExpiredLatch() {
  authExpiredLatched = false;
  isShowingLoginPrompt = false;
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(AUTH_EXPIRED_KEY);
  } catch (_) { /* ignore */ }
}

function getPathname() {
  try {
    return (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  } catch (_) {
    return '';
  }
}

/** 登录/注册接口的 401 表示凭据错误，不是会话过期 */
function isCredentialAuthUrl(url) {
  const u = String(url || '');
  return /\/user\/login(?:\?|$)/i.test(u)
    || /\/auth\/login(?:\?|$)/i.test(u)
    || /\/user\/register(?:\?|$)/i.test(u)
    || /\/register(?:\?|$)/i.test(u);
}

function isOnLoginPage() {
  const p = getPathname();
  return p === '/login' || p === '/register';
}

/**
 * 处理401未授权：清除token、提示用户、跳转登录。
 * 注意：toast 时长为 3 秒；若在 onClose 里用 pathname+search 与 '/login' 比较，
 * 已在 /login?redirect=... 时会误触发整页硬跳转，表现为「过两三秒闪一下」。
 */
function handleUnauthorized() {
  if (readExpiredLatch() || isShowingLoginPrompt) return;
  isShowingLoginPrompt = true;
  setExpiredLatch();

  // 清除本地的token
  try { localStorage.removeItem('token'); } catch (_) { /* ignore */ }

  const goLogin = () => {
    isShowingLoginPrompt = false;
    if (typeof window === 'undefined' || !window.location) return;
    if (isOnLoginPage()) return; // 只看 pathname，忽略 ?redirect=
    const currentPath = window.location.pathname + window.location.search;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath || '/')}`;
  };

  // 动态导入message以避免循环依赖；立刻安排跳转，避免等 toast 3 秒期间轮询继续 401
  const redirectTimer = setTimeout(goLogin, 50);

  import('./antdMessage').then(({ message }) => {
    message.warning('登录已过期，请重新登录', 3, () => {
      isShowingLoginPrompt = false;
      // 若 50ms 跳转未执行（已在登录页），仅收尾
      if (!isOnLoginPage()) {
        clearTimeout(redirectTimer);
        goLogin();
      }
    });
  }).catch(() => {
    clearTimeout(redirectTimer);
    goLogin();
  });
}

/**
 * 统一API请求工具
 * @param {string} url - 请求地址（相对路径会自动加上基础URL）
 * @param {object} options - fetch选项
 * @returns {Promise<Response>} fetch的Promise
 */
export default async function apiFetch(url, options = {}) {
  // 确保路径以/开头，避免重复加/api/
  let cleanUrl = url;
  if (cleanUrl.startsWith('/api/')) {
    // 如果路径以/api/开头，移除/api部分，因为API_BASE_URL已经包含了/api
    cleanUrl = cleanUrl.substring(4);
  } else if (!cleanUrl.startsWith('/') && !cleanUrl.startsWith('http')) {
    // 如果路径不以/开头也不是绝对URL，加上/
    cleanUrl = '/' + cleanUrl;
  }
  
  // 构建完整URL
  const fullUrl = cleanUrl.startsWith('http') ? cleanUrl : `${API_BASE_URL}${cleanUrl}`;
  
  // 默认带上 token；FormData 不要手动设 Content-Type，否则 multipart 边界丢失
  const token = localStorage.getItem('token');
  const skipSessionExpiry = isCredentialAuthUrl(fullUrl) || isCredentialAuthUrl(cleanUrl);
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers = {
    ...(options.headers || {})
  };
  if (isFormData) {
    const uploadHeaders = {};
    if (token) uploadHeaders.Authorization = `Bearer ${token}`;
    console.log('API请求:', { url: fullUrl, method: options.method || 'GET', formData: true });
    const response = await fetch(fullUrl, { ...options, headers: uploadHeaders });
    
    // 处理401响应（登录口除外）
    if (response.status === 401 && !skipSessionExpiry) {
      handleUnauthorized();
    }
    
    return response;
  }
  if (headers['Content-Type'] == null && headers['content-type'] == null) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers.Authorization = `Bearer ${token}`;

  console.log('API请求:', { url: fullUrl, method: options.method || 'GET' });
  const response = await fetch(fullUrl, { ...options, headers });
  
  // 处理401响应（登录口除外）
  if (response.status === 401 && !skipSessionExpiry) {
    handleUnauthorized();
  }
  
  return response;
}